import React from 'react';
import { Modal } from 'antd-mobile';
import { client } from '../graphql/client';
import { CHECK_APP_UPDATE, CheckAppUpdateResponse, CheckAppUpdateVariables, UpdateContentType } from '../graphql/queries/checkAppUpdate';
import { getAppVersion } from './appRuntime';
import { isIOS, isAndroid } from './deviceUtils';
import { isLoggedIn } from './auth';
import { t } from '../i18n';
import i18n from '../i18n';
import UpgradeGuideContent from '../components/UpgradeGuide/UpgradeGuideContent';

/**
 * 根据当前语言从多语言描述对象中获取对应语言的更新说明
 */
function getUpdateContentByLang(updateContent: UpdateContentType | null): string {
    if (!updateContent) return '';
    const lang = (i18n.language || 'en').split('-')[0];
    const langMap: Record<string, string | null> = {
        zh: updateContent.zh,
        en: updateContent.en,
        es: updateContent.es
    };
    return langMap[lang] || updateContent.en || '';
}

/**
 * 获取当前 App 的平台类型
 */
function getAppType(): string {
    if (isIOS()) return 'mobile_ios';
    if (isAndroid()) return 'mobile_android';
    return 'mobile_android';
}

/**
 * 显示升级指引弹窗（分平台展示 App Store / Google Play 操作步骤）
 */
function showUpgradeGuide(isForceUpdate: boolean) {
    const platform = isIOS() ? 'ios' as const : 'android' as const;

    const contentElement = React.createElement(UpgradeGuideContent, { platform });

    const handler = Modal.show({
        content: contentElement,
        closeOnMaskClick: false,
        showCloseButton: false,
        bodyStyle: { padding: 0, background: 'transparent' },
        actions: isForceUpdate
            ? []
            : [
                {
                    key: 'later',
                    text: t('appVersion.dialog.later') as string,
                    style: { color: '#ffffff', fontWeight: 600 },
                    onClick: () => handler.close(),
                },
            ],
    });
}

/**
 * 显示版本更新弹窗
 * 展示版本号、更新内容，点击"升级指引"按钮打开分平台的操作步骤
 */
function showUpdateDialog(
    versionName: string,
    updateContent: UpdateContentType | null,
    isForceUpdate: boolean
) {
    const content = getUpdateContentByLang(updateContent);

    const contentElement = React.createElement('div', null,
        React.createElement('div', { style: { marginBottom: 8, fontWeight: 600 } },
            `${t('appVersion.dialog.versionLabel')}: ${versionName}`
        ),
        content ? React.createElement('div', null,
            React.createElement('div', { style: { fontWeight: 600, marginBottom: 4 } },
                `${t('appVersion.dialog.updateContent')}:`
            ),
            React.createElement('div', { style: { whiteSpace: 'pre-wrap', color: '#666', fontSize: 14 } },
                content
            )
        ) : null
    );

    const handler = Modal.show({
        title: t('appVersion.dialog.title') as string,
        content: contentElement,
        closeOnMaskClick: false,
        actions: isForceUpdate
            ? [
                {
                    key: 'guide',
                    text: t('appVersion.dialog.upgradeGuide') as string,
                    primary: true,
                    onClick: () => {
                        handler.close();
                        showUpgradeGuide(isForceUpdate);
                    },
                },
            ]
            : [
                {
                    key: 'later',
                    text: t('appVersion.dialog.later') as string,
                    onClick: () => handler.close(),
                },
                {
                    key: 'guide',
                    text: t('appVersion.dialog.upgradeGuide') as string,
                    primary: true,
                    onClick: () => {
                        handler.close();
                        showUpgradeGuide(isForceUpdate);
                    },
                },
            ],
    });
}

/**
 * 检查并显示版本更新弹窗
 * 调用时机：用户登录后 / App 进程重新启动时
 *
 * 流程：
 * 1. 获取当前 App 版本号，调用后端 checkAppUpdate 接口（后端内部包含灰度检查）
 * 2. 如果需要更新，弹出更新提示框
 */
export async function checkAndShowVersionUpdate(): Promise<void> {
    if (!isLoggedIn()) return;

    try {
        // 获取 App 版本号并检查更新
        const appVersionStr = await getAppVersion();
        const appType = getAppType();

        const { data: updateData } = await client.query<
            CheckAppUpdateResponse,
            CheckAppUpdateVariables
        >({
            query: CHECK_APP_UPDATE,
            variables: { type: appType, versionName: appVersionStr },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
        });

        const result = updateData?.checkAppUpdate;
        if (!result?.needUpdate || !result.latestVersion) return;

        // 步骤 3：弹出版本更新提示
        showUpdateDialog(
            result.latestVersion.versionName,
            result.latestVersion.updateContent,
            result.isForceUpdate
        );
    } catch (error) {
        // 版本检查失败不影响正常使用，静默忽略
        console.warn('Version update check failed:', error);
    }
}
